import { useFetch } from "./utils";
import { visionURL } from "../config/environment";
import visionCallService from "../services/visionCallService";
import Jsona from "jsona";
import JsonApiService from "../services/jsonapiService";
import { denormalizeTableEntity } from "../redux/utils/jsona";
import normalize from "../redux/utils/normalize";

const dataFormatter = new Jsona();

const homeworkSubmissionApiService = async () =>
  new JsonApiService("homework_submissions", {
    baseUrl: `${visionURL}/admin_console/api/administration`,
    credentials: "include"
  });

const homeworkSubmissionCommentApiService = async () =>
  new JsonApiService("homework_submissions", {
    baseUrl: `${visionURL}/admin_console/api/content`,
    credentials: "include"
  });

const defaultOption = { value: "All", label: "All" };
export const useEmployeeStates = () => {
  return [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    defaultOption
  ];
};

export const useHomeworkStates = () => {
  return [
    { value: "submitted", label: "Submitted" },
    { value: "accepted", label: "Accepted" },
    { value: "rejected", label: "Rejected" },
    defaultOption
  ];
};

export const useFolders = training_object_id => {
  const contentables = useContentables(training_object_id);
  return contentables
    ? [defaultOption, ...createOptions(contentables, "name")]
    : contentables;
};

const createOptions = ({ data }, labelKey) => {
  return (
    data &&
    data.map(datum => ({
      value: datum.id,
      label: datum.attributes[labelKey]
    }))
  );
};

const useContentables = training_object_id => {
  const apiUrl = `${visionURL}/admin_console/api/content/courses/${training_object_id}/course_contentables/`;
  const homework = useFetch(apiUrl);
  return homework;
};

export const useHomeworkSubmission = () => {
  return {
    getHomeworkSubmission: async homeworkSubmissionId => {
      const service = await homeworkSubmissionApiService();
      const response = await service
        .include("comments")
        .find(homeworkSubmissionId)
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    }
  };
};

export const useHomeworkSubmissionComment = () => {
  return {
    getHomeworkSubmissionComment: async homeworkSubmissionId => {
      const service = await homeworkSubmissionCommentApiService();
      const response = await service
        .related(homeworkSubmissionId, "comments")
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    createHomeworkSubmissionComment: async (
      homeworkSubmissionId,
      newCommentData
    ) => {
      const service = await homeworkSubmissionCommentApiService();
      const response = await service
        .createRelated(
          homeworkSubmissionId,
          "comments",
          visionCallService.serializeRequestBody(newCommentData, "comments")
        )
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    updateHomeworkSubmissionComment: async (
      homeworkSubmissionId,
      commentId,
      newCommentData
    ) => {
      const service = await homeworkSubmissionCommentApiService();
      const response = await service
        .updateRelatedResource(
          homeworkSubmissionId,
          "comments",
          commentId,
          visionCallService.serializeRequestBody(
            { ...newCommentData, id: commentId },
            "comments"
          )
        )
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    }
  };
};

const cleanQuery = query => {
  let cleanedQuery = {};
  Object.entries(query).forEach(([key, val]) => {
    cleanedQuery[key] = val === defaultOption.value ? "" : val;
  });
  return cleanedQuery;
};

export const useHomeworkSubmissionsTableData = (
  training_object_id,
  query,
  changes
) => {
  let apiUrl = `${visionURL}/admin_console/api/content/courses/${training_object_id}/homework_submissions`;
  apiUrl += `?${visionCallService.buildQueryString(cleanQuery(query))}`;
  const homework = useFetch(
    apiUrl,
    changes,
    query && JSON.stringify(query) !== "{}"
  );
  return homework
    ? denormalizeTableEntity(normalize(homework), "homeworkSubmission").data
    : homework;
};

export const useDownloadAll = (training_object_id, selection) => {
  let downloadUrl = `${visionURL}/admin_console/api/content/courses/${training_object_id}/homework_submission_downloads`;
  if (selection) {
    downloadUrl += "?";
    selection.forEach(id => {
      downloadUrl += `&homeworkSubmissions[]=${id}`;
    });
  }

  const downloadAll = () => {
    var win = window.open(downloadUrl, "_blank");
    win.focus();
  };
  return downloadAll;
};
export const useUpdateStatus = (training_object_id, submission_id) => {
  const apiUrl = `${visionURL}/admin_console/api/content/courses/${training_object_id}/homework_submissions/${submission_id}`;
  const updateStatus = async data => {
    const response = await visionCallService.putData(data, apiUrl);
    return response.status === 200;
  };
  return updateStatus;
};

const homeworkApiService = async () =>
  new JsonApiService("homework", {
    baseUrl: `${visionURL}/admin_console/api/course_builder`,
    credentials: "include"
  });

const bulkHomeworkAttachmentsApiService = async () =>
  new JsonApiService("bulk_homework_assignment_attachments", {
    baseUrl: `${visionURL}/admin_console/api/course_builder`,
    credentials: "include"
  });

export const useHomework = () => {
  return {
    getHomework: async homeworkId => {
      const service = await homeworkApiService();
      const response = await service
        .find(homeworkId)
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    createHomework: async newHomeworkData => {
      const service = await homeworkApiService();
      const response = await service
        .create(
          visionCallService.serializeRequestBody(
            {
              ...newHomeworkData,
              relationshipNames: ["subscribedEmails", "fileFormats"]
            },
            "homework",
            ["subscribedEmails", "fileFormats"]
          )
        )
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    updateHomework: async newHomeworkData => {
      const service = await homeworkApiService();
      const response = await service
        .update(
          newHomeworkData.id,
          visionCallService.serializeRequestBody(
            {
              ...newHomeworkData,
              relationshipNames: ["subscribedEmails", "fileFormats"]
            },
            "homework",
            ["subscribedEmails", "fileFormats"]
          )
        )
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    bulkHomeworkAttachments: async (homework, attachments) => {
      const service = await bulkHomeworkAttachmentsApiService();
      await service.create(
        visionCallService.serializeRequestBody(
          {
            homework,
            attachments,
            relationshipNames: ["attachments", "homework"]
          },
          "bulkHomeworkAttachments"
        )
      );
    }
  };
};
